import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Info,
  GraduationCap,
  Wallet,
  Briefcase,
  BookOpen,
  Gift,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';

/**
 * 앱 공통 상단 통합 메뉴바 (대메뉴 6개 + 드롭다운 소메뉴).
 * 2026-10-10: 기존 13개 단일 탭 나열 방식에서 목업('메뉴바 및 교육 메인')의 통합 드롭다운으로 교체.
 *
 * 아직 페이지가 없는 항목은 `to` 없이 두면 자동으로 비활성(회색·클릭 불가) 처리된다.
 * 페이지가 생기면 `to`만 채우면 된다.
 */

interface MenuItem {
  label: string;
  /** 없으면 미구현 항목 — 클릭 불가 */
  to?: string;
}

interface MenuGroup {
  label: string;
  icon: LucideIcon;
  /** 목업의 대메뉴별 아이콘 색상 */
  iconColor: string;
  /** 지식 챌린지의 느낌표 뱃지 */
  alert?: boolean;
  items: MenuItem[];
}

const MENU_GROUPS: MenuGroup[] = [
  {
    label: '소개',
    icon: Info,
    iconColor: 'text-sky-500',
    items: [
      { label: '스탬핏이란', to: '/about' },
      { label: '입점 교육 기관', to: '/partners/education' },
      { label: '입점 채용 기관', to: '/partners/recruitment' },
      { label: '우수 인증 교육 기관', to: '/premium-providers' },
      { label: '우수 인증 교육 기관 신청' },
      { label: '교육 기관 전용 페이지 신청' },
      { label: 'Q&A' },
      { label: '문의 / 오류 신고' },
    ],
  },
  {
    label: '교육',
    icon: GraduationCap,
    iconColor: 'text-orange-500',
    items: [
      { label: '교육 메인', to: '/?view=classes&tab=main' },
      { label: '자격 과정', to: '/?view=classes&tab=certificate' },
      { label: '수료 과정', to: '/?view=classes&tab=completion' },
      { label: '워크샵 (세미나)', to: '/?view=classes&tab=workshop' },
      { label: '온라인 교육 (100%)', to: '/?view=classes&tab=online' },
    ],
  },
  {
    label: '커리어 관리',
    icon: Wallet,
    iconColor: 'text-rose-600',
    items: [
      { label: '인증서 보관함 (Career wallet)', to: '/?view=classes&tab=wallet' },
      { label: '레벨 뱃지 (Level Badge)', to: '/?view=classes&tab=badge' },
    ],
  },
  {
    label: '채용',
    icon: Briefcase,
    iconColor: 'text-emerald-600',
    items: [
      { label: '구인구직', to: '/?view=jobs' },
      { label: '대강 (기업 등록)', to: '/?view=substitute' },
      { label: '대강 (강사 등록)', to: '/?view=substitute' },
      { label: '나의 이력서 (Career Passport)', to: '/profile/resume' },
    ],
  },
  {
    label: '지식 챌린지',
    icon: BookOpen,
    iconColor: 'text-blue-600',
    alert: true,
    items: [
      { label: '기능해부학', to: '/?view=challenge' },
      { label: '생활스포츠지도사' },
      { label: '운동생리학' },
      { label: '운동학' },
      { label: '운동처방' },
      { label: '병태생리학' },
    ],
  },
  {
    label: '런칭 이벤트',
    icon: Gift,
    iconColor: 'text-amber-600',
    items: [
      { label: '교육 기관 회원' },
      { label: '기업 회원' },
      { label: '개인 회원' },
    ],
  },
];

/**
 * 메뉴 항목의 경로가 현재 위치와 같은 화면을 가리키는지 판단.
 * HomePage는 쿼리스트링으로 화면을 전환하므로(`/?view=...&tab=...`) 쿼리까지 비교하되,
 * 쿼리가 없는 `/`는 HomePage 기본값(교육 메인)과 같은 것으로 본다.
 */
function isItemActive(to: string, pathname: string, search: string): boolean {
  const [toPath, toQuery = ''] = to.split('?');
  if (toPath !== pathname) return false;
  if (!toQuery) return true;

  const current = new URLSearchParams(search);
  const target = new URLSearchParams(toQuery);
  // HomePage 기본값 보정 — `/`로 들어오면 view=classes&tab=main 과 동일
  const currentView = current.get('view') || 'classes';
  const currentTab = current.get('tab') || 'main';

  for (const [key, value] of target.entries()) {
    const currentValue = key === 'view' ? currentView : key === 'tab' ? currentTab : current.get(key);
    if (currentValue !== value) return false;
  }
  return true;
}

interface MainTabsNavProps {
  // Layout(Header/Footer) 밖에서 자체 <Header/>를 렌더하는 페이지용 — 고정 헤더(h-16) 높이만큼
  // 자연 위치를 밀어내야 sticky의 containing block(직계 부모)이 페이지 전체 높이를 갖고,
  // 스크롤 중에도 계속 붙어있는다. 래퍼 div로 감싸면 containing block이 그 래퍼로 좁아져
  // 스크롤 조금만 해도 탭바가 붙어있지 못하고 사라지므로, 반드시 margin으로 처리할 것.
  className?: string;
}

export default function MainTabsNav({ className = '' }: MainTabsNavProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const navRef = useRef<HTMLElement>(null);

  // 바깥 클릭 / ESC 로 드롭다운 닫기
  useEffect(() => {
    if (openIndex === null) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpenIndex(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenIndex(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openIndex]);

  // 화면이 바뀌면 열린 메뉴를 닫는다
  useEffect(() => setOpenIndex(null), [pathname, search]);

  const go = (to: string) => {
    setOpenIndex(null);
    navigate(to);
    window.scrollTo(0, 0);
  };

  return (
    <div className={`sticky top-16 z-40 bg-gray-50 border-b border-gray-200 mb-4 ${className}`}>
      <nav
        ref={navRef}
        aria-label="통합 메뉴"
        className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-start md:justify-center gap-1.5 py-2 overflow-x-auto md:overflow-x-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {MENU_GROUPS.map((group, index) => {
          const Icon = group.icon;
          const isOpen = openIndex === index;
          const hasActiveItem = group.items.some((item) => item.to && isItemActive(item.to, pathname, search));

          return (
            <div key={group.label} className="relative shrink-0">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-haspopup="true"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-sm font-extrabold whitespace-nowrap transition-colors ${
                  isOpen || hasActiveItem ? 'bg-accent-50 text-accent-600' : 'text-gray-700 hover:bg-accent-50 hover:text-accent-600'
                }`}
              >
                {group.alert ? (
                  <span className="relative inline-flex w-5 h-5 items-center justify-center shrink-0" aria-hidden="true">
                    <Icon className={`w-[1.1rem] h-[1.1rem] ${group.iconColor}`} />
                    <span className="absolute -top-1.5 -right-1.5 grid place-items-center w-3.5 h-3.5 rounded-full bg-blue-600 text-white text-[9px] font-black leading-none ring-2 ring-gray-50">
                      !
                    </span>
                  </span>
                ) : (
                  <Icon className={`w-[1.1rem] h-[1.1rem] ${group.iconColor}`} />
                )}
                {group.label}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180 text-accent-600' : 'text-gray-400'}`}
                />
              </button>

              {isOpen && (
                <div className="absolute top-[calc(100%+0.45rem)] left-1/2 -translate-x-1/2 z-50 w-max min-w-[13rem] max-w-[calc(100vw-2rem)] p-2 rounded-2xl border border-gray-200 bg-white shadow-[0_18px_42px_rgba(15,23,42,0.16)]">
                  {group.items.map((item) => {
                    const available = Boolean(item.to);
                    const active = item.to ? isItemActive(item.to, pathname, search) : false;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        disabled={!available}
                        aria-disabled={!available}
                        title={available ? undefined : '준비 중입니다'}
                        onClick={available ? () => go(item.to!) : undefined}
                        className={`block w-full px-3 py-2.5 rounded-lg text-left text-[0.8rem] font-semibold transition-colors ${
                          !available
                            ? 'text-gray-300 cursor-default'
                            : active
                              ? 'bg-accent-50 text-accent-600'
                              : 'text-gray-600 hover:bg-accent-50 hover:text-accent-600'
                        }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
